import React from 'react';
import { Star, Trophy, Brain, ArrowRight, Target, Zap, Heart, Award, Calculator, Lightbulb, Smile, FlaskRound as Flask, BookOpen, Sparkles, Info, Users, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import UselessFact from '../components/UselessFact';
import RandomGenerator from '../components/RandomGenerator';
import DailyChallenge from '../components/DailyChallenge';
import AdUnit from '../components/AdUnit';
import AnimalFacts from '../components/AnimalFacts';
import WeirdScience from '../components/WeirdScience';
import TopAchievement from '../components/TopAchievement';
import PointlessRecords from '../components/PointlessRecords';
import UselessInventions from '../components/UselessInventions';
import NumberFacts from '../components/NumberFacts';
import NewsletterForm from '../components/NewsletterForm';
import { useAuth } from '../hooks/useAuth';

const MainLayout = () => {
  const { session } = useAuth();

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Introduction Section */}
      <section className="mb-12">
        <h1 className="text-4xl font-bold gradient-text text-center mb-6">
          Welcome to The Most Useless Website
        </h1>
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <p className="text-xl text-gray-300">
            Discover mind-blowing useless facts, weird animal trivia, and fascinating time-wasters. 
            Your daily dose of entertaining knowledge that you never knew you needed!
          </p>
          <p className="text-gray-400">
            Join thousands of others who come here daily to learn interesting but completely impractical facts, 
            challenge themselves with pointless achievements, and become part of our growing community of 
            useless knowledge enthusiasts.
          </p>
        </div>
      </section>

      {/* Top Ad Unit */}
      <AdUnit 
        slot="1234567890" 
        format="auto"
        position="top"
        className="mb-12"
      />

      {/* How It Works Section */}
      <section className="mb-12">
        <div className="card p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-cyan-500/20">
              <Info className="h-8 w-8 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold gradient-text">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-black/20 rounded-lg p-6">
              <div className="text-3xl font-bold text-blue-400 mb-4">1</div>
              <h3 className="text-lg font-semibold text-white mb-2">Explore Facts</h3>
              <p className="text-gray-300 text-sm">
                Browse through our extensive collection of useless but fascinating facts 
                across multiple categories. Each fact is verified but completely impractical.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6">
              <div className="text-3xl font-bold text-green-400 mb-4">2</div>
              <h3 className="text-lg font-semibold text-white mb-2">Complete Challenges</h3>
              <p className="text-gray-300 text-sm">
                Take on daily challenges that serve no real purpose but are surprisingly 
                entertaining. Each completion earns you points and potential achievements.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6">
              <div className="text-3xl font-bold text-purple-400 mb-4">3</div>
              <h3 className="text-lg font-semibold text-white mb-2">Earn Points</h3>
              <p className="text-gray-300 text-sm">
                Like facts, complete challenges, and maintain daily streaks to earn points. 
                Watch your score grow as you discover more useless knowledge.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6">
              <div className="text-3xl font-bold text-orange-400 mb-4">4</div>
              <h3 className="text-lg font-semibold text-white mb-2">Unlock Achievements</h3>
              <p className="text-gray-300 text-sm">
                Collect unique achievements as you interact with the site. Each achievement 
                comes with bonus points and a special badge to showcase your dedication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Content Section */}
      <section className="mb-12">
        <div className="card p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
              <Sparkles className="h-8 w-8 text-purple-400" />
            </div>
            <h2 className="text-2xl font-bold gradient-text">Featured Content</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-black/20 rounded-lg p-6 hover:bg-black/30 transition">
              <BookOpen className="h-6 w-6 text-purple-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Daily Facts</h3>
              <p className="text-gray-300 text-sm">
                Every day we bring you new and fascinating facts that are completely useless 
                but utterly entertaining. From animal oddities to scientific curiosities, 
                there's always something new to discover.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6 hover:bg-black/30 transition">
              <Target className="h-6 w-6 text-green-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Daily Challenges</h3>
              <p className="text-gray-300 text-sm">
                Push yourself with our daily challenges that serve no practical purpose 
                but are surprisingly addictive. Earn points, unlock achievements, and 
                compete with others in our leaderboard.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6 hover:bg-black/30 transition">
              <Trophy className="h-6 w-6 text-yellow-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Achievements</h3>
              <p className="text-gray-300 text-sm">
                Collect badges and earn points for exploring useless knowledge. Our 
                achievement system turns procrastination into a rewarding experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {!session && (
        <section className="mb-12">
          <div className="card p-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-pink-500/5" />
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-purple-500/10 to-transparent" />
            
            <div className="relative">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                  <Trophy className="h-8 w-8 text-purple-400" />
                </div>
                <h1 className="text-3xl font-bold gradient-text">Level Up Your Procrastination!</h1>
              </div>

              <div className="bg-black/20 rounded-lg p-4 mb-8">
                <div className="flex flex-wrap gap-6">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center space-x-2 mb-3">
                      <Star className="h-5 w-5 text-yellow-400" />
                      <span className="text-sm font-semibold text-gray-300">Points</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center space-x-1">
                        <Heart className="h-3 w-3 text-red-400" />
                        <span className="text-gray-400">Like: <strong className="text-white">10pts</strong></span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Target className="h-3 w-3 text-green-400" />
                        <span className="text-gray-400">Challenge: <strong className="text-white">20pts</strong></span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Award className="h-3 w-3 text-purple-400" />
                        <span className="text-gray-400">Achieve: <strong className="text-white">10-300pts</strong></span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Zap className="h-3 w-3 text-orange-400" />
                        <span className="text-gray-400">Streak: <strong className="text-white">15-250pts</strong></span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center space-x-2 mb-3">
                      <Trophy className="h-5 w-5 text-purple-400" />
                      <span className="text-sm font-semibold text-gray-300">Categories</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center space-x-1">
                        <Brain className="h-3 w-3 text-blue-400" />
                        <span className="text-gray-400">Facts</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Heart className="h-3 w-3 text-red-400" />
                        <span className="text-gray-400">Animals</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Flask className="h-3 w-3 text-indigo-400" />
                        <span className="text-gray-400">Science</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Trophy className="h-3 w-3 text-yellow-400" />
                        <span className="text-gray-400">Records</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Lightbulb className="h-3 w-3 text-amber-400" />
                        <span className="text-gray-400">Inventions</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calculator className="h-3 w-3 text-cyan-400" />
                        <span className="text-gray-400">Numbers</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Smile className="h-3 w-3 text-pink-400" />
                        <span className="text-gray-400">Excuses</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Target className="h-3 w-3 text-green-400" />
                        <span className="text-gray-400">Challenges</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                <Link 
                  to="/register"
                  className="w-full md:w-auto gradient-bg text-white px-6 py-2.5 rounded-lg hover:opacity-90 transition flex items-center justify-center space-x-2"
                >
                  <span>Start Your Journey</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <Link 
                  to="/login"
                  className="text-gray-300 hover:text-white transition text-center w-full md:w-auto"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {session && (
        <section className="mb-12">
          <TopAchievement />
        </section>
      )}

      {/* Main content grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        <UselessFact />
        <AnimalFacts />
        <WeirdScience />
        <PointlessRecords />
        <UselessInventions />
        <NumberFacts />
      </div>

      {/* Mid-page Ad Unit */}
      <AdUnit 
        slot="5678901234" 
        format="auto"
        position="middle"
        className="mb-12"
      />

      {/* Bottom content grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        <RandomGenerator />
        <DailyChallenge />
        <div className="card p-6 hover-lift">
          <h3 className="text-xl font-semibold gradient-text mb-4">Did You Know?</h3>
          <ul className="space-y-4">
            <li className="flex items-start space-x-2">
              <span className="text-purple-400">•</span>
              <span className="text-gray-300">Bananas are berries, but strawberries aren't</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-purple-400">•</span>
              <span className="text-gray-300">Honey never spoils. Ever.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-purple-400">•</span>
              <span className="text-gray-300">Cows have best friends and get stressed when separated</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Why Join Section */}
      <section className="mb-12">
        <div className="card p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-500/20">
              <Users className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold gradient-text">Why Join Us?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-black/20 rounded-lg p-6">
              <Rocket className="h-6 w-6 text-green-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Track Progress</h3>
              <p className="text-gray-300 text-sm">
                Create an account to track your achievements, maintain streaks, and compete 
                with others in our global leaderboard of useless knowledge.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6">
              <Trophy className="h-6 w-6 text-yellow-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Earn Rewards</h3>
              <p className="text-gray-300 text-sm">
                Unlock exclusive achievements, earn points, and showcase your dedication 
                to learning things that serve absolutely no practical purpose.
              </p>
            </div>
            <div className="bg-black/20 rounded-lg p-6">
              <Users className="h-6 w-6 text-blue-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Join Community</h3>
              <p className="text-gray-300 text-sm">
                Connect with fellow enthusiasts who share your passion for useless knowledge. 
                Share discoveries and compete in daily challenges together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="mb-12">
        <div className="card p-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-center md:text-left space-y-6 md:space-y-0">
            <div>
              <h3 className="text-xl font-bold gradient-text">Get Daily Useless Facts!</h3>
              <p className="text-gray-300">Join 10,000+ others getting useless knowledge daily.</p>
            </div>
            <NewsletterForm className="w-full md:w-auto" />
          </div>
        </div>
      </section>

      {/* Bottom Ad Unit */}
      <AdUnit 
        slot="3456789012" 
        format="auto"
        position="bottom"
      />
    </div>
  );
};

export default MainLayout;